import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class TaskQueueReadOnly extends ModelObject<TaskQueueReadOnly> {

    /**
    * Sequence for ordering task queues.
    */
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class TaskQueue extends ModelObjectWithDirtyCheck<TaskQueue> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Queue Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;
    public readOnly: TaskQueueReadOnly = new TaskQueueReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
          this.readOnly = new TaskQueueReadOnly(jsonSerializedObject.readOnly);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "TaskQueue.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class TaskQueueService extends ServiceBase {

  public async getTaskQueues(taskQueueIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TaskQueue)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskQueueIds:taskQueueIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.taskQueues.map((v: any) => new TaskQueue(v));;
    return this.makeJsonRequest("taskQueue/v1/getTaskQueues", req, false, extractor, opts);
  };

  public async getTaskQueue(taskQueueId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<TaskQueue> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskQueueId:taskQueueId.toString(),
    };
    const extractor = (response:any) => new TaskQueue(response.taskQueue);;
    return this.makeJsonRequest("taskQueue/v1/getTaskQueue", req, false, extractor, opts);
  };

  public async getAllTaskQueues(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TaskQueue)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.taskQueues.map((v: any) => new TaskQueue(v));;
    return this.makeJsonRequest("taskQueue/v1/getAllTaskQueues", req, false, extractor, opts);
  };

  public async saveTaskQueue(taskQueue: TaskQueue, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskQueue:taskQueue.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("taskQueue/v1/saveTaskQueue", req, true, extractor, opts);
  };

  public async setTaskQueueIsHidden(taskQueueId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskQueueId:taskQueueId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("taskQueue/v1/setTaskQueueIsHidden", req, true, extractor, opts);
  };

  public async updateTaskQueuesSequence(taskQueueIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    taskQueueIds:taskQueueIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("taskQueue/v1/updateTaskQueuesSequence", req, true, extractor, opts);
  };
}

export const taskQueueService = new TaskQueueService();